import type { RelationshipStatus } from "@kontextwerk/control-web-client"

/**
 * KB view of one organization membership: the identity contract plus the optional e-mail the
 * server only fills for core.memberships manage. The e-mail is display data, never authority.
 */
export type MembershipAccess = Readonly<{
  id: string
  user_id: string
  organization_id: string
  role_id: string
  status: RelationshipStatus
  revision: number
  email: string | null
}>

export type MembershipAccessChange = Readonly<{
  membership: MembershipAccess
  changed: boolean
}>

const MEMBERSHIP_FIELDS = [
  "id",
  "user_id",
  "organization_id",
  "role_id",
  "status",
  "revision",
  "email",
] as const

export const invalid = (): never => {
  throw new TypeError("Invalid membership access contract")
}

const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return invalid()

  return value as Record<string, unknown>
}

export const uuid = (value: unknown): string => {
  if (typeof value !== "string" || !/^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/i.test(value))
    return invalid()

  return value.toLowerCase()
}

/** Display-only: an absent, null or implausible address stays unknown instead of hiding the row. */
const optionalEmail = (value: unknown): string | null =>
  typeof value === "string" && value.length <= 320 && /^[^\s@]+@[^\s@]+$/.test(value) ? value : null

export const membershipAccess = (value: unknown, organizationId: string): MembershipAccess => {
  const input = record(value)

  if (Object.keys(input).some((key) => !(MEMBERSHIP_FIELDS as readonly string[]).includes(key)))
    return invalid()

  if (uuid(input.organization_id) !== uuid(organizationId)) return invalid()

  if (input.status !== "active" && input.status !== "suspended") return invalid()

  if (
    typeof input.revision !== "number" ||
    !Number.isSafeInteger(input.revision) ||
    input.revision < 1
  )
    return invalid()

  return Object.freeze({
    id: uuid(input.id),
    user_id: uuid(input.user_id),
    organization_id: uuid(input.organization_id),
    role_id: uuid(input.role_id),
    status: input.status,
    revision: input.revision,
    email: optionalEmail(input.email),
  })
}

export const membershipAccessChange = (
  value: unknown,
  organizationId: string,
  membershipId: string,
): MembershipAccessChange => {
  const input = record(value)

  if (Object.keys(input).some((key) => key !== "membership" && key !== "changed")) return invalid()

  if (typeof input.changed !== "boolean") return invalid()

  const membership = membershipAccess(input.membership, organizationId)

  if (membership.id !== uuid(membershipId)) return invalid()

  return Object.freeze({ membership, changed: input.changed })
}
